import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MapPin, X, ChevronLeft, ChevronRight
} from 'lucide-react';

const AddListingView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { listingData } = location.state || {};

  // Jeśli nie ma danych, wróć do formularza
  useEffect(() => {
    if (!listingData) {
      navigate('/createlisting');
    }
  }, [listingData, navigate]);

  // Podstawowe stany
  const [selectedImage, setSelectedImage] = useState(0);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  // Nowo dodane stany:
  const [listingType, setListingType] = useState('standard');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [carConditionConfirmed, setCarConditionConfirmed] = useState(false);

  // Funkcje obsługi zdjęć
  const handlePrevImage = () => {
    setSelectedImage((prev) =>
      prev > 0 ? prev - 1 : listingData.photos.length - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImage((prev) =>
      prev < listingData.photos.length - 1 ? prev + 1 : 0
    );
  };

  const openPhotoModal = (index) => {
    setPhotoIndex(index);
    setIsPhotoModalOpen(true);
  };

  const closePhotoModal = () => {
    setIsPhotoModalOpen(false);
  };

  const nextPhoto = () => {
    setPhotoIndex((prev) =>
      prev < listingData.photos.length - 1 ? prev + 1 : 0
    );
  };

  const prevPhoto = () => {
    setPhotoIndex((prev) =>
      prev > 0 ? prev - 1 : listingData.photos.length - 1
    );
  };

  // Obsługa publikacji (sprawdzenie checkboxów)
  const handlePublish = () => {
    if (!acceptedTerms || !carConditionConfirmed) {
      alert('Proszę zaznaczyć wymagane zgody (regulamin i stan auta).');
      return;
    }
    alert(`Ogłoszenie opublikowane z opcją: ${listingType}`);
  };

  // Komponent pomocniczy do wyświetlania wiersza info
  const InfoRow = ({ label, value }) => (
    <div className="p-2 bg-gray-50 rounded-sm">
      <div className="text-gray-600 font-medium">{label}</div>
      <div className="font-semibold text-black">{value || 'Nie podano'}</div>
    </div>
  );

  // Jeśli listingData jest wczytywany asynchronicznie, wstaw warunek:
  if (!listingData) {
    return null; // lub loader
  }

  // Render
  return (
    <div className="bg-[#FCFCFC] py-8 px-4 lg:px-[15%]">
      {/* Informacja o podglądzie */}
      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
        <p className="text-yellow-700 font-medium">
          Podgląd ogłoszenia - zobacz jak będzie wyglądało Twoje ogłoszenie po publikacji
        </p>
      </div>

      {/* Modal ze zdjęciami */}
      {isPhotoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={closePhotoModal} />
          <div className="relative text-center max-w-5xl w-full mx-4">
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
              onClick={closePhotoModal}
              title="Zamknij (Esc)"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="flex justify-between items-center mt-10">
              <button
                onClick={prevPhoto}
                className="bg-white/90 p-2 hover:bg-white transition-colors rounded-sm"
                title="Poprzednie zdjęcie"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <img
                src={listingData.photos[photoIndex]}
                alt={`Zdjęcie powiększone ${photoIndex + 1}`}
                className="max-h-[85vh] w-auto mx-4 rounded-sm"
              />
              <button
                onClick={nextPhoto}
                className="bg-white/90 p-2 hover:bg-white transition-colors rounded-sm"
                title="Następne zdjęcie"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </div>
            <div className="text-white mt-4 text-sm">
              Zdjęcie {photoIndex + 1} z {listingData.photos.length}
            </div>
          </div>
        </div>
      )}

      {/* Główna zawartość */}
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Lewa strona */}
          <div className="w-full lg:w-[60%] space-y-8">
            {/* Galeria zdjęć */}
            <div className="bg-white p-4 shadow-md rounded-sm">
              <div className="relative aspect-video mb-4">
                <img
                  src={listingData.photos[selectedImage]}
                  alt={`Główne zdjęcie ${selectedImage + 1}`}
                  className="w-full h-full object-cover rounded-sm cursor-pointer"
                  onClick={() => openPhotoModal(selectedImage)}
                />
                <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevImage();
                    }}
                    className="bg-white/80 p-2 hover:bg-white transition-colors rounded-sm"
                    title="Poprzednie"
                  >
                    <ChevronLeft className="w-6 h-6 text-black" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                    className="bg-white/80 p-2 hover:bg-white transition-colors rounded-sm"
                    title="Następne"
                  >
                    <ChevronRight className="w-6 h-6 text-black" />
                  </button>
                </div>
                <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded-sm text-sm">
                  {selectedImage + 1} / {listingData.photos.length}
                </div>
              </div>

              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {listingData.photos.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`
                      relative aspect-video overflow-hidden rounded-sm
                      ${selectedImage === index ? 'ring-2 ring-black' : ''}
                    `}
                  >
                    <img
                      src={img}
                      alt={`Miniatura ${index + 1}`}
                      className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Opis */}
            <div className="bg-white p-6 shadow-md rounded-sm">
              <h2 className="text-xl md:text-2xl font-bold mb-4 text-black">
                Opis pojazdu
              </h2>
              <div className="leading-relaxed text-gray-700 whitespace-pre-line text-lg">
                {listingData.description}
              </div>
            </div>
          </div>

          {/* Prawa strona */}
          <div className="w-full lg:w-[40%] space-y-4">
            {/* Tytuł i cena */}
            <div className="bg-white p-4 shadow-md rounded-sm">
              <div className="bg-gradient-to-r from-[#E8E8E8] to-[#FEFEFE] p-4 rounded-sm">
                <h1 className="text-2xl font-bold text-black mb-2">
                  {/* Możesz dodać listingData.title, jeśli masz taką zmienną */}
                  Ogłoszenie samochodu
                </h1>
                <div className="text-3xl font-black text-black tracking-tight">
                  {listingData.purchaseOption === 'najem'
                    ? `${listingData.rentalPrice} PLN/mc`
                    : `${listingData.price} PLN`}
                </div>
              </div>
            </div>

            {/* Dane techniczne */}
            <div className="bg-white p-4 shadow-md rounded-sm">
              <h2 className="text-lg font-bold mb-3 text-black px-2">
                Dane techniczne
              </h2>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <InfoRow label="Stan" value={listingData.condition} />
                <InfoRow label="Rok produkcji" value={listingData.productionYear} />
                <InfoRow label="Przebieg" value={`${listingData.mileage} km`} />
                <InfoRow label="Paliwo" value={listingData.fuelType} />
                <InfoRow label="Moc" value={`${listingData.power} KM`} />
                <InfoRow label="Skrzynia biegów" value={listingData.transmission} />
                <InfoRow label="Napęd" value={listingData.drive} />
                <InfoRow label="Kraj pochodzenia" value={listingData.countryOfOrigin} />
              </div>
            </div>

            {/* Lokalizacja */}
            <div className="bg-white p-6 shadow-md rounded-sm">
              <h2 className="text-lg font-bold mb-4 text-black">Lokalizacja</h2>
              <div className="text-gray-700 text-lg flex items-center justify-center gap-2">
                <MapPin className="w-6 h-6" />
                <span>
                  {listingData.city}, {listingData.voivodeship}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NOWA SEKCJA: WYBÓR RODZAJU OGŁOSZENIA + CHECKBOXY + PRZYCISK OPUBLIKUJ */}
      <div className="max-w-6xl mx-auto mt-8 bg-white p-4 shadow-md rounded-sm">
        <h3 className="text-xl font-semibold mb-4">Rodzaj ogłoszenia i zgody</h3>

        {/* Rodzaj ogłoszenia (standard/premium) */}
        <div className="flex flex-col sm:flex-row gap-6 mb-6">
          <label className="flex-1 border p-4 rounded cursor-pointer hover:bg-gray-50">
            <div className="flex items-center">
              <input
                type="radio"
                name="listingType"
                value="standard"
                checked={listingType === 'standard'}
                onChange={(e) => setListingType(e.target.value)}
                className="mr-2 accent-[#35530A]"
              />
              <span className="font-medium">OGŁOSZENIE STANDARDOWE</span>
            </div>
            <div className="text-center mt-2 text-[#35530A]">
              30 zł / 30 dni
            </div>
          </label>

          <label className="flex-1 border p-4 rounded cursor-pointer hover:bg-gray-50">
            <div className="flex items-center">
              <input
                type="radio"
                name="listingType"
                value="premium"
                checked={listingType === 'premium'}
                onChange={(e) => setListingType(e.target.value)}
                className="mr-2 accent-[#35530A]"
              />
              <span className="font-medium">OGŁOSZENIE WYRÓŻNIONE</span>
            </div>
            <div className="text-center mt-2 text-[#35530A]">
              50 zł / 30 dni
            </div>
          </label>
        </div>

        <div className="space-y-3">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 mr-2 accent-[#35530A]"
            />
            <span className="text-sm">
              Oświadczam, że zapoznałem(-am) się z <b>Regulaminem serwisu</b> i akceptuję jego postanowienia.
            </span>
          </label>

          <label className="flex items-start">
            <input
              type="checkbox"
              checked={carConditionConfirmed}
              onChange={(e) => setCarConditionConfirmed(e.target.checked)}
              className="mt-1 mr-2 accent-[#35530A]"
            />
            <span className="text-sm">
              Oświadczam, że stan samochodu jest zgodny z opisem i stanem faktycznym.
            </span>
          </label>
        </div>

        {/* Przyciski Akcji - na dole */}
        <div className="mt-6 flex justify-center gap-4">
          <button
            onClick={() =>
              navigate('/createlisting', { state: { listingData } })
            }
            className="
              bg-gray-100 text-gray-700
              px-6 py-3
              rounded-lg
              hover:bg-gray-200
              transition-all
              duration-200
            "
          >
            ← Wróć do edycji
          </button>

          <button
            onClick={handlePublish}
            className="
              bg-green-600 text-white
              px-6 py-3
              rounded-lg
              hover:bg-green-700
              transition-all
              duration-200
            "
          >
           Przejdź do płatności →
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddListingView;
